import { useMemo } from 'react'
import classNames from 'classnames'
import { ArrowRightIcon } from '@heroicons/react/24/outline'

import { Button } from '@elements/button'
import { PosterImage } from '@elements/poster-image'
import { TimePlaceDisplay } from '@components/time-place-display'
import { Address } from 'types/common'
import { Skeleton } from '@elements/skeleton'
import useRequest from '@hooks/request'

export interface EventCardProps {
  layout?: 'vertical' | 'horizontal'
  featured?: boolean
  poster?: string
  name: string
  eventId: string
  venueName: string
  dateTime: string
  timezone?: string
  venueAddress?: Address
  onClick: (eventId: string) => void
}

function _EventCard({
  poster,
  name,
  featured = false,
  eventId,
  venueName,
  dateTime,
  timezone,
  venueAddress,
  layout = 'vertical',
  onClick,
}: EventCardProps) {
  const layoutStyles = useCardLayout({ layout, featured })

  const [handleClick, handlingClick] = useRequest(async () => {
    if (!eventId) return

    await onClick(eventId)
  }, [eventId, onClick])

  if (!poster) return null
  return (
    <div className='relative'>
      {featured && (
        <span className='absolute -top-2 -right-2 px-2 py-1 bg-primary-300 text-white z-10 border border-slate-light uppercase rounded-md drop-shadow-lg text-sm font-bold shimmer'>
          Featured
        </span>
      )}
      <div className={layoutStyles.container} onClick={handleClick}>
        <div className={layoutStyles.poster}>
          <span className='absolute inset-0 z-0 w-full h-full sm:shimmer'></span>
          <PosterImage
            className='relative z-[1]'
            alt={name}
            src={poster}
            h={layout === 'horizontal' ? '1500' : '1250'}
            w='1000'
          />
        </div>
        <div className={layoutStyles.details}>
          <div className={layoutStyles.titleContainer}>
            <span role='title' className={layoutStyles.title}>
              {name}
            </span>
          </div>
          <div className='w-full space-y-3 !text-left'>
            <TimePlaceDisplay
              dayClassName={layoutStyles.day}
              dateTime={dateTime}
              timezone={timezone}
              address={venueAddress}
              place={venueName}
            />
            <Button.Primary
              className={layoutStyles.cta}
              rightIcon={ArrowRightIcon}
              sm
              loading={handlingClick}
            >
              Book Now
            </Button.Primary>
          </div>
        </div>
      </div>
    </div>
  )
}

function EventCardSkeleton({
  layout = 'vertical',
}: { layout?: 'vertical' | 'horizontal' }) {
  const layoutStyles = useCardLayout({ layout })
  return (
    <div className='w-[240px] h-[474px] bg-card shadow-md dark:shadow-stone-950/[0.8]'>
      <Skeleton className='w-full aspect-[4/5] rounded-b-none max-w-[240px]' />
      <div className={layoutStyles.details}>
        <div className={layoutStyles.titleContainer}>
          <span role='title' className={layoutStyles.title}>
            <Skeleton className='w-40 h-5' />
          </span>
        </div>
        <div className='w-full space-y-3'>
          <div className='flex flex-col space-y-1.5'>
            <Skeleton className='w-20 h-5' />
            <Skeleton className='w-20 h-4' />
            <Skeleton className='w-20 h-4' />
          </div>
          <Skeleton className='w-full h-5' />
        </div>
      </div>
    </div>
  )
}

export const EventCard = Object.assign(_EventCard, {
  Skeleton: EventCardSkeleton,
})

function useCardLayout({
  layout = 'vertical',
  featured = false,
}: {
  layout: 'vertical' | 'horizontal'
  featured?: boolean
}) {
  const horizontal = layout === 'horizontal'
  const vertical = layout === 'vertical'

  return useMemo(
    () => ({
      container: classNames(
        'group w-full flex justify-start items-start overflow-clip rounded-md cursor-pointer',
        'transition-all duration-300 sm:delay-200 ease-out bg-card dark:shadow-stone-950/[0.8]',
        'shadow-md',
        {
          'flex-col max-w-[270px] min-[370px]:max-w-[360px] min-w-[214px] min-[370px]:min-w-[240px]':
            vertical,
          'flex-row max-w-[440px] min-w-[360px] aspect-[10/7] ': horizontal,
          'border-2 border-primary-600': featured,
        }
      ),
      poster: classNames('relative z-0', {
        'w-full aspect-[4/5] bg-neutral-300': vertical,
        'h-full max-h-[320px] aspect-[2/3]': horizontal,
      }),
      details: classNames(
        'flex flex-col items-start justify-between p-3 space-y-3',
        {
          'w-full': vertical,
          'h-full w-2/3': horizontal,
        }
      ),
      titleContainer: classNames(
        'will-change-[max-height] transition-all overflow-clip',
        'ease-in-out sm:delay-75 duration-300',
        {
          'min-h-[40px] max-h-[40px] group-[.inspecting]:max-h-[96px]':
            vertical,
          'min-h-[48px] max-h-[72px] group-[.inspecting]:max-h-[144px]':
            horizontal,
        }
      ),
      title: classNames(
        'font-bold text-basic transition-all will-change-[line-clamp] sm:delay-200',
        'group-[.inspecting]:line-clamp-4 group-[.inspecting]:delay-0',
        {
          'line-clamp-2 text-md': vertical,
          'line-clamp-3 text-lg': horizontal,
        }
      ),
      day: classNames('text-base font-bold', {
        'text-base': vertical,
        'text-body': horizontal,
      }),
      cta: classNames('w-full uppercase'),
    }),
    [horizontal, vertical]
  )
}
